import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { Calendar } from "../components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import {
  FileBarChart, Download, Eye, DollarSign, TrendingUp, CalendarDays, Loader2,
  ArrowDownLeft, ArrowUp, ArrowDown, Columns3, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const $ = (v) => v == null ? "$0.00" : `$${Number(v).toFixed(2)}`;
const $4 = (v) => v == null ? "$0.0000" : `$${Number(v).toFixed(4)}`;
const pct = (v) => v == null ? "0%" : `${Number(v).toFixed(1)}%`;
const num = (v) => v == null ? "0" : Number(v).toLocaleString();

const ALL_COLUMNS = [
  { key: "date", label: "Date", sticky: true, default: true },
  { key: "ssp_endpoint", label: "SSP Endpoint", default: true },
  { key: "ssp_company", label: "SSP Company", default: true },
  { key: "ssp_endpoint_type", label: "SSP EP Type", default: false },
  { key: "ssp_endpoint_url", label: "SSP EP URL", default: false },
  { key: "dsp_endpoint", label: "DSP Endpoint", default: true },
  { key: "dsp_company", label: "DSP Company", default: false },
  { key: "dsp_endpoint_type", label: "DSP EP Type", default: false },
  { key: "dsp_endpoint_url", label: "DSP EP URL", default: false },
  { key: "geo", label: "GEO", default: true },
  { key: "traffic_type", label: "Traffic Type", default: true },
  { key: "ad_format_size", label: "Ad Format Size", default: true },
  { key: "domain_bundle", label: "Domain/App Bundle", default: true },
  { key: "publisher_id", label: "Publisher ID", default: false },
  { key: "crid", label: "CRID", default: false },
  { key: "os", label: "OS", default: true },
  { key: "ssp_requests", label: "SSP Requests", align: "right", format: num, default: true },
  { key: "ssp_requests_with_uid", label: "SSP Req w/ UID", align: "right", format: num, default: false },
  { key: "synced_cookies", label: "Synced Cookies", align: "right", format: num, default: false },
  { key: "bid_requests", label: "Bid Requests", align: "right", format: num, default: true },
  { key: "buyer_ids", label: "Buyer IDs", align: "right", format: num, default: false },
  { key: "dsp_responses", label: "DSP Responses", align: "right", format: num, default: true },
  { key: "dsp_wins", label: "DSP Wins", align: "right", format: num, default: false },
  { key: "dsp_win_rate", label: "DSP Win Rate %", align: "right", format: pct, color: "text-emerald-600", default: true },
  { key: "ssp_wins", label: "SSP Wins", align: "right", format: num, default: false },
  { key: "ssp_win_rate", label: "SSP Win Rate %", align: "right", format: pct, color: "text-blue-600", default: false },
  { key: "clicks", label: "Clicks", align: "right", format: num, default: false },
  { key: "impressions", label: "Impressions", align: "right", format: num, color: "text-blue-600", default: true },
  { key: "fill_rate", label: "Fill Rate %", align: "right", format: pct, color: "text-amber-600", default: true },
  { key: "render_rate", label: "Render Rate %", align: "right", format: pct, default: false },
  { key: "ssp_spend", label: "SSP Spend", align: "right", format: $, color: "text-red-600", default: true },
  { key: "ssp_ecpm", label: "SSP eCPM", align: "right", format: $4, default: false },
  { key: "ssp_rcpm", label: "SSP RCPM", align: "right", format: $4, default: false },
  { key: "avg_ssp_bidfloor", label: "Avg SSP Bidfloor", align: "right", format: $4, default: true },
  { key: "avg_ssp_bid_price", label: "Avg SSP Bid Price", align: "right", format: $4, default: false },
  { key: "dsp_spend", label: "DSP Spend", align: "right", format: $, color: "text-emerald-600", default: true },
  { key: "dsp_ecpm", label: "DSP eCPM", align: "right", format: $4, default: false },
  { key: "dsp_rcpm", label: "DSP RCPM", align: "right", format: $4, default: false },
  { key: "avg_dsp_bidfloor", label: "Avg DSP Bidfloor", align: "right", format: $4, default: true },
  { key: "avg_dsp_bid_price", label: "Avg DSP Bid Price", align: "right", format: $4, default: false },
  { key: "profit", label: "Profit", align: "right", format: $, color: "text-violet-600", default: true },
  { key: "valid_bid_rate", label: "Valid Bid Rate %", align: "right", format: pct, default: false },
  { key: "total_bid_rate", label: "Total Bid Rate %", align: "right", format: pct, default: false },
  { key: "video_complete", label: "Video Complete", align: "right", format: num, default: false },
  { key: "ctr_rate", label: "CTR Rate %", align: "right", format: pct, default: false },
  { key: "vcr_rate", label: "VCR Rate %", align: "right", format: pct, default: false },
];

const PAGE_SIZES = [25, 50, 100, 200];

export default function ReportingPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("7d");
  const [sspFilter, setSspFilter] = useState("");
  const [dspFilter, setDspFilter] = useState("");
  // "standard" = geo / format / size / OS (hourly data); "domain" = domain/bundle + publisher (daily data)
  const [breakdown, setBreakdown] = useState("standard");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Sorting
  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  // Column chooser
  const [visibleCols, setVisibleCols] = useState(() =>
    ALL_COLUMNS.filter(c => c.default).map(c => c.key)
  );
  const [showColPicker, setShowColPicker] = useState(false);

  const fetchReport = useCallback(async (overrideStart, overrideEnd) => {
    setLoading(true);
    try {
      const s = overrideStart || customRange.start;
      const e = overrideEnd || customRange.end;
      let url = `/reports/detailed?period=${period}`;
      if (period === "custom" && s && e)
        url = `/reports/detailed?period=custom&start_date=${s}&end_date=${e}`;
      if (sspFilter && sspFilter !== "all") url += `&ssp_id=${sspFilter}`;
      if (dspFilter && dspFilter !== "all") url += `&dsp_id=${dspFilter}`;
      url += `&breakdown=${breakdown}`;
      const res = await api.get(url);
      setData(res.data);
      setPage(1);
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  }, [api, period, sspFilter, dspFilter, breakdown, customRange.start, customRange.end]);

  // Don't auto-fetch — user must click "Generate Report"

  // Sorted rows
  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    return [...data.rows].sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (va == null) va = "";
      if (vb == null) vb = "";
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      return sortDir === "asc"
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
  }, [data, sortKey, sortDir]);

  // Paginated rows
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const paginatedRows = sortedRows.slice((page - 1) * pageSize, page * pageSize);

  // Active columns
  const activeCols = ALL_COLUMNS.filter(c => visibleCols.includes(c.key));

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const toggleCol = (key) => {
    setVisibleCols(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handlePeriod = (v) => {
    if (v === "custom") {
      setPeriod("custom");
      setShowDatePicker(true);
    } else {
      setPeriod(v);
      setShowDatePicker(false);
    }
  };
  const applyCustom = () => {
    if (!dateRange.from || !dateRange.to) { toast.error("Select both start and end dates on the calendar"); return; }
    const start = dateRange.from.toISOString().slice(0, 10);
    const end = dateRange.to.toISOString().slice(0, 10);
    setCustomRange({ start, end });
    setShowDatePicker(false);
    // Pass dates directly — don't rely on async state update
    fetchReport(start, end);
  };

  const formatDateLabel = () => {
    if (dateRange.from && dateRange.to) {
      const f = dateRange.from.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const t = dateRange.to.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      return `${f} - ${t}`;
    }
    if (dateRange.from) {
      return `${dateRange.from.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - pick end`;
    }
    return "Pick date range";
  };

  const exportCSV = () => {
    if (!sortedRows.length) return;
    const cols = activeCols.map(c => c.label);
    const csvRows = [cols.join(",")];
    sortedRows.forEach(r => {
      csvRows.push(activeCols.map(c => {
        const v = r[c.key];
        return typeof v === "string" && v.includes(",") ? `"${v}"` : (v ?? "");
      }).join(","));
    });
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `report_${data?.date_range?.start}_${data?.date_range?.end}.csv`;
    a.click();
    toast.success("Report exported");
  };

  const m = (n) => n == null ? "$0.00" : `$${Number(n).toFixed(2)}`;

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="reporting-loading">
      <div className="flex items-center gap-3"><Loader2 className="h-8 w-8 animate-spin text-accent" /><span className="text-muted-foreground">Generating report...</span></div>
    </div>
  );

  const t = data?.totals || { ssp_requests: 0, impressions: 0, dsp_responses: 0, ssp_spend: 0, dsp_spend: 0, profit: 0, fill_rate: 0 };

  return (
    <div className="space-y-5" data-testid="reporting-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileBarChart className="h-7 w-7 text-accent" /> Reports
          </h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            {data ? `${data.date_range?.start} — ${data.date_range?.end}` : "Select filters and click Generate Report"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* SSP Filter */}
          <Select value={sspFilter || "all"} onValueChange={v => setSspFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="ssp-filter-select"><SelectValue placeholder="All SSPs" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All SSPs</SelectItem>
              {(data?.ssps || []).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {/* DSP Filter */}
          <Select value={dspFilter || "all"} onValueChange={v => setDspFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="dsp-filter-select"><SelectValue placeholder="All DSPs" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All DSPs</SelectItem>
              {(data?.dsps || []).map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {/* Period */}
          <Select value={breakdown} onValueChange={setBreakdown}>
            <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="report-breakdown-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">By Geo / Format / OS</SelectItem>
              <SelectItem value="domain">By Domain / Bundle</SelectItem>
            </SelectContent>
          </Select>

          <Select value={period === "custom" ? "custom" : period} onValueChange={handlePeriod}>
            <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="report-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          {period === "custom" && (
            <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 min-w-[200px] justify-start" data-testid="date-range-btn">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {formatDateLabel()}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="p-3 border-b">
                  <p className="text-sm font-medium">Select Date Range</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Click start date, then click end date</p>
                </div>
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => setDateRange(range || { from: undefined, to: undefined })}
                  numberOfMonths={2}
                  disabled={dateRange.from && !dateRange.to ? { before: dateRange.from } : undefined}
                  data-testid="date-range-calendar"
                />
                <div className="p-3 border-t flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {dateRange.from && dateRange.to
                      ? `${dateRange.from.toLocaleDateString()} — ${dateRange.to.toLocaleDateString()}`
                      : "Select start and end dates"}
                  </span>
                  <Button size="sm" className="h-7" onClick={applyCustom} disabled={!dateRange.from || !dateRange.to} data-testid="report-apply-btn">
                    Apply
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}
          {/* Column Chooser */}
          <Popover open={showColPicker} onOpenChange={setShowColPicker}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8" data-testid="column-chooser-btn">
                <Columns3 className="h-3.5 w-3.5 mr-1" /> Columns ({visibleCols.length})
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="end">
              <div className="p-3 border-b flex items-center justify-between">
                <span className="text-sm font-medium">Choose Columns</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setVisibleCols(ALL_COLUMNS.map(c => c.key))}>All</Button>
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setVisibleCols(ALL_COLUMNS.filter(c => c.default).map(c => c.key))}>Default</Button>
                </div>
              </div>
              <ScrollArea className="h-[350px]">
                <div className="p-2 space-y-0.5">
                  {ALL_COLUMNS.map(col => (
                    <label key={col.key} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer text-sm">
                      <Checkbox
                        checked={visibleCols.includes(col.key)}
                        onCheckedChange={() => toggleCol(col.key)}
                        data-testid={`col-toggle-${col.key}`}
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
          {/* Generate + CSV */}
          <Button size="sm" className="h-8" onClick={() => fetchReport()} disabled={loading} data-testid="generate-report-btn">
            {loading ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Generating...</> : <><Eye className="h-3.5 w-3.5 mr-1" />Generate Report</>}
          </Button>
          <Button variant="outline" size="sm" className="h-8" onClick={exportCSV} disabled={!data?.rows?.length} data-testid="export-csv-btn">
            <Download className="h-3.5 w-3.5 mr-1" /> CSV
          </Button>
        </div>
      </div>

      {/* Totals — show only when data loaded */}
      {data ? (
      <>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <MC icon={Eye} label="SSP Requests" value={t.ssp_requests.toLocaleString()} color="" />
        <MC icon={Eye} label="Impressions" value={t.impressions.toLocaleString()} color="text-blue-600" />
        <MC icon={ArrowDownLeft} label="SSP Spend" value={m(t.ssp_spend)} color="text-red-600" />
        <MC icon={DollarSign} label="DSP Spend" value={m(t.dsp_spend)} color="text-emerald-600" />
        <MC icon={TrendingUp} label="Profit" value={m(t.profit)} color="text-violet-600" />
        <MC icon={Eye} label="Fill Rate" value={`${t.fill_rate}%`} color="text-amber-600" />
      </div>

      {/* Table */}
      <Card data-testid="report-table-card">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Detailed Report ({sortedRows.length} rows)</CardTitle>
          {/* Pagination controls (top) */}
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Rows:</span>
            <Select value={String(pageSize)} onValueChange={v => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger className="w-[70px] h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map(s => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-muted/50 border-b">
                  {activeCols.map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      className={`px-2 py-2 font-medium whitespace-nowrap border-r border-border/30 cursor-pointer hover:bg-muted/80 select-none ${col.align === "right" ? "text-right" : "text-left"} ${col.sticky ? "sticky left-0 bg-muted/50 z-10" : ""}`}
                      data-testid={`sort-${col.key}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {sortKey === col.key && (
                          sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row, i) => (
                  <tr key={i} className="border-b hover:bg-muted/20" data-testid={`report-row-${i}`}>
                    {activeCols.map(col => (
                      <td key={col.key} className={`px-2 py-1.5 whitespace-nowrap font-mono border-r border-border/10 ${col.align === "right" ? "text-right" : ""} ${col.color || ""} ${col.sticky ? "sticky left-0 bg-background z-10" : ""}`}>
                        {col.format ? col.format(row[col.key]) : (row[col.key] || "—")}
                      </td>
                    ))}
                  </tr>
                ))}
                {paginatedRows.length === 0 && (
                  <tr><td colSpan={activeCols.length} className="text-center py-12 text-muted-foreground text-sm">No data</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {sortedRows.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/10" data-testid="pagination">
              <span className="text-xs text-muted-foreground">
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, sortedRows.length)} of {sortedRows.length}
              </span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage(1)} data-testid="page-first">
                  <ChevronLeft className="h-3 w-3" /><ChevronLeft className="h-3 w-3 -ml-1.5" />
                </Button>
                <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="page-prev">
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="text-xs px-2 font-medium">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="page-next">
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
                <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage(totalPages)} data-testid="page-last">
                  <ChevronRight className="h-3 w-3" /><ChevronRight className="h-3 w-3 -ml-1.5" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      </>
      ) : (
        <Card className="py-16">
          <CardContent className="text-center text-muted-foreground">
            <FileBarChart className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">Select filters and click "Generate Report"</p>
            <p className="text-sm mt-1">Choose period, SSP, DSP filters above, then generate</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function MC({ icon: Icon, label, value, color }) {
  return (
    <Card>
      <CardContent className="pt-3 pb-2 px-3">
        <p className="text-[10px] text-muted-foreground uppercase flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</p>
        <p className={`text-lg font-bold mt-0.5 ${color}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
